import type { CardholderOnboardingTranslations } from "../interfaces/cardholderOnboarding";

export const cardholderOnboardingTranslations: CardholderOnboardingTranslations = {
  page_title: "Cardholder onboarding",
  page_subtitle:
    "Take each of an organization's cardholders from registration to being able to hold a card. Every row says where they are and what to do next.",
  action_refresh: "Refresh",
  retry: "Try again",
  cancel: "Cancel",
  close: "Close",
  unnamed: "No name",
  provider_unknown: "the card issuer",
  error_generic: "We couldn't complete the action. Try again in a moment.",

  scope_label: "Organization",
  scope_placeholder: "Search by name, email or ID",
  scope_loading: "Loading organizations…",
  scope_empty: "No organization matches",
  scope_searching: "Searching…",
  scope_search_error: "We couldn't search organizations",
  scope_catalog_error: "We couldn't load the organizations",
  scope_catalog_truncated: "Only the most recent are listed; type to search the rest",
  scope_required_hint: "Pick the organization whose cardholders you want to review",
  scope_org_hint: "Cardholders of this organization",
  summary_line: "{{name}} · issues with {{provider}} · {{count}} cardholders",

  pick_org_title: "Pick an organization",
  pick_org_description:
    "You'll see each of its cardholders, the step they are at and what they need to hold a card.",
  load_error: "We couldn't load this organization's cardholders.",
  denied_error: "Your user is not allowed to see the cardholders.",
  unsupported_notice:
    "This organization issues with {{provider}}: its cardholders are registered when they are created, so only failed registrations are retried here.",
  truncated_notice: "The organization has more than 2,000 cardholders; only the most recent are shown.",

  bucket_ready: "Ready to send",
  bucket_paused: "Paused",
  bucket_needs_verification: "No verification",
  bucket_needs_data: "Missing data",
  bucket_registration_failed: "Registration failed",
  bucket_in_progress: "In progress",
  bucket_enabled: "Enabled",
  bucket_closed: "Closed",

  search_placeholder: "Search by name, document or email",
  clear_filters: "Show all",
  empty_organization: "This organization has no cardholders yet",
  empty_bucket: "No cardholders in this group",
  empty_search: "No cardholder matches “{{term}}”",

  column_holder: "Cardholder",
  column_progress: "Progress",
  column_next: "Next step",
  own_identity_badge: "The organization",
  select_page: "Select the ones on this page",
  select_row: "Select {{name}}",
  bulk_selected: "Selected: {{count}}",
  bulk_clear: "Clear selection",
  bulk_send: "Send to {{provider}}",

  track_label: "Cardholder progress",
  step_registration: "Registration",
  step_verification: "Verification",
  state_done: "done",
  state_current: "in progress",
  state_blocked: "held",
  state_failed: "failed",
  state_pending: "pending",

  headline_ready: "Verified and ready to send to {{provider}}.",
  headline_ready_resume: "Verified; left halfway at {{provider}} and can be resumed.",
  headline_paused: "Paused at {{provider}}.",
  headline_paused_reason: "Paused at {{provider}}: {{reason}}.",
  headline_verification_missing: "No identity verification is linked.",
  headline_verification_not_approved: "Their verification is not approved here.",
  headline_verification_other_service:
    "Their verification was made with another identity service; it cannot be sent from here.",
  headline_verification_incomplete: "Their verification record is incomplete; review it with the tech team.",
  headline_missing_email: "The organization has no email for this person.",
  headline_missing_identity_fields: "Missing the last name or the date of birth.",
  headline_registration_failed: "Their registration as a cardholder did not finish.",
  headline_in_progress: "{{provider}} is processing them.",
  headline_in_review: "{{provider}} is reviewing their documents.",
  headline_enabled: "Enabled: they can hold cards now.",
  headline_identity_rejected: "Their identity was rejected.",
  headline_card_rejected: "{{provider}} rejected them.",
  headline_business: "A company: {{provider}} only gives cards to people.",
  headline_closed: "There is nothing to do with this cardholder.",

  action_send: "Send",
  action_resume: "Resume",
  action_link_verification: "Link verification",
  action_sync_verification: "Check verification",
  action_complete_data: "See what's missing",
  action_retry_registration: "Retry registration",

  skip_already_enabled: "Already enabled",
  skip_already_rejected: "They were rejected",
  skip_no_kyc_record: "No identity verification",
  skip_kyc_not_verified: "Their verification is not approved",
  skip_no_kyc_applicant: "Their verification is not linked",
  skip_missing_identity_fields: "Missing the last name or the date of birth",
  skip_missing_client_email: "The organization has no email for them",
  skip_limit_reached: "Left out by this send's limit",
  skip_concurrent_conflict: "Another send took them at the same time",
  skip_error: "Could not be queued",
  skip_changed: "Their status changed; refresh the board",

  kyc_PENDING: "pending",
  kyc_IN_REVIEW: "in review",
  kyc_VERIFIED: "approved",
  kyc_REJECTED: "rejected",
  kyc_EXPIRED: "expired",
  no_kyc: "No verification",

  send_title: "Send to {{provider}}",
  send_description: "We first check who can be sent; nothing goes out until you confirm.",
  send_checking: "Checking who can be sent…",
  send_ready: "Ready to send: {{count}}.",
  send_queue_note:
    "They are registered at {{provider}} one at a time, in the same line as new cards, so their status changes within a few minutes.",
  send_none: "None can be sent yet.",
  send_paused_note: "Were paused: {{count}}. Unless the reason was fixed, they will pause again.",
  send_skipped_heading: "Not sent ({{count}}):",
  send_confirm: "Send ({{count}})",
  send_recheck: "Check again",
  send_done_heading: "Sent",
  send_done_counts: "First time: {{queued}}. Resumed: {{resumed}}.",
  send_done_skipped: "Left out while sending: {{count}}. Check them again on the board.",
  send_done_note: "Refresh the board in a few minutes to see their progress at {{provider}}.",

  link_title: "Link verification",
  link_description: "Finds {{name}}'s identity verification and links it to their record.",
  link_searching: "Looking for their verification…",
  link_found: "We found a verification for this person.",
  link_match_by: "Matched by {{field}}",
  match_external_user_id: "their Coloca record",
  match_document_number: "document number",
  match_birthdate_name: "name and date of birth",
  match_email: "email",
  match_manual: "manual link",
  link_confirm: "Link",
  link_done: "Verification linked.",
  link_done_verified: "Verification linked and approved. You can send them to {{provider}} now.",
  link_other_identity:
    "That verification belongs to another person registered in Coloca; it is not linked here.",
  link_linked_elsewhere:
    "The verification ended up linked to another person registered in Coloca, not this one. Review it with compliance.",
  link_category_linked: "Verification linked.",
  link_category_linkable: "The verification can be linked.",
  link_category_superseded: "There is a more recent approved verification for this person.",
  link_category_match_unverified:
    "A verification has the same email, but neither the document nor the date of birth match; it is not linked automatically.",
  link_category_ambiguous: "The verification matches more than one person; review it with compliance.",
  link_category_foreign_external_id: "The verification is under another identity.",
  link_category_already_linked: "They already have a verification linked; check its status.",
  link_category_other_provider: "Their verification was made with another identity service.",
  link_category_no_applicant: "We found no verification made from Coloca for this person.",
  link_category_orphan: "The verification matches nobody registered in Coloca.",
  link_category_applicant_not_found: "There is no verification with that ID.",
  link_category_error: "We couldn't look up the verification.",
  link_manual_label: "Do you have the verification ID?",
  link_manual_placeholder: "Verification ID",
  link_manual_search: "Search",
  link_manual_hint:
    "For verifications made outside Coloca. It is only linked when the document, or the name and date of birth, match.",
  link_manual_invalid: "Check the ID: letters, digits, hyphens and underscores only, 6 to 64 characters.",
  link_org_identity_hint:
    "This is the organization's own identity. If its verification was made outside Coloca, link it from Account verification.",
  link_org_identity_open: "Go to Account verification",

  sync_title: "Check verification",
  sync_description: "Compares the status of {{name}}'s verification with the one we have stored.",
  sync_checking: "Comparing with the verification…",
  sync_mismatch: "The verification reads {{remote}}, but here it is {{local}}.",
  sync_in_sync: "Both here and in the verification it reads {{status}}; there is nothing to update.",
  sync_not_reviewed: "The verification has not been reviewed yet.",
  sync_no_reference: "No verification is linked.",
  sync_applicant_not_found:
    "The linked verification no longer exists and there is no approved one to use; review it with the tech team.",
  sync_other_provider: "Their verification was made with another identity service.",
  sync_green_sibling:
    "This person has another approved verification; you can use it instead of the current one.",
  sync_apply: "Change to {{status}}",
  sync_repoint: "Use the approved verification",
  sync_recheck: "Check again",
  sync_updated: "Status updated: {{status}}.",
  sync_repointed: "It now uses the approved verification.",

  data_title: "What's missing",
  data_missing_identity_fields:
    "{{name}} is missing the last name or the date of birth, and cannot be registered without them. Fill them in on their client record and send them again.",
  data_missing_email:
    "The organization has no email for {{name}}. Add it on their client record and send them again.",
  data_ok: "Got it",

  retry_title: "Retry registration",
  retry_description:
    "Registers {{name}} again with the data already stored. If it works, they move on to verification and then to {{provider}}.",
  retry_done: "Registration completed: {{name}}.",

  detail_verification: "Verification",
  detail_card: "At {{provider}}",
  detail_email: "Contact email",
  detail_country: "Country",
  detail_updated: "Last change at the issuer",
  detail_created: "Created",
  detail_identity_id: "Identity ID",
};
